import React, { forwardRef } from 'react';
import { alpha, Box, ButtonBase, IconButton, Tooltip } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

interface FilterPillProps {
  label: string;
  icon: React.ReactNode;
  /** The filter's value in words; empty when it is not set. */
  summary: string;
  /** Set, but not applied: a date range whose "to" is before its "from". */
  warning?: boolean;
  /** Its panel is open. */
  open: boolean;
  /** A count instead of a summary, as on "More filters". */
  badge?: number;
  onOpen: () => void;
  /** The ×; omitted where the pill cannot be cleared on its own. */
  onClear?: () => void;
}

/**
 * One filter on the bar: its name, and once set its value and a ×. The panel
 * that edits it opens below (FilterSurface), anchored to this pill.
 */
export const FilterPill = forwardRef<HTMLDivElement, FilterPillProps>(
  ({ label, icon, summary, warning, open, badge, onOpen, onClear }, ref) => {
    const active = Boolean(summary);

    return (
      <Box
        ref={ref}
        sx={(theme) => {
          const tone = warning ? theme.palette.warning.main : theme.palette.primary.main;
          return {
            display: 'inline-flex',
            alignItems: 'center',
            height: 34,
            maxWidth: '100%',
            borderRadius: 999,
            border: '1px solid',
            borderColor: active || warning ? alpha(tone, 0.45) : '#d6dce6',
            backgroundColor: active || warning ? alpha(tone, 0.07) : 'background.paper',
            color: warning ? theme.palette.warning.dark : active ? theme.palette.primary.dark : 'text.primary',
            boxShadow: open ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` : '0 1px 2px rgba(15, 23, 42, 0.04)',
            transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
            '&:hover': { borderColor: active || warning ? tone : '#94a3b8' },
          };
        }}
      >
        <ButtonBase
          onClick={onOpen}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={active ? `${label}: ${summary}` : badge ? `${label} (${badge} set)` : label}
          sx={{
            height: '100%',
            minWidth: 0,
            pl: 1.25,
            pr: active && onClear ? 0.5 : 1,
            gap: 0.75,
            borderRadius: 999,
            fontSize: '0.8125rem',
            fontWeight: 500,
            lineHeight: 1,
            '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
          }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              display: 'inline-flex',
              color: warning ? 'warning.main' : active ? 'primary.main' : 'text.secondary',
              '& svg': { fontSize: 17 },
            }}
          >
            {warning ? <WarningAmberRoundedIcon /> : icon}
          </Box>
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {label}
          </Box>
          {active && (
            <>
              <Box component="span" aria-hidden sx={{ width: '1px', alignSelf: 'stretch', my: 0.75, bgcolor: 'currentColor', opacity: 0.25 }} />
              <Box
                component="span"
                title={summary}
                sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 150, sm: 240 } }}
              >
                {summary}
              </Box>
            </>
          )}
          {!active && badge ? (
            <Box
              component="span"
              sx={{
                minWidth: 18,
                height: 18,
                px: 0.5,
                borderRadius: 999,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: '0.6875rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {badge}
            </Box>
          ) : null}
          {!(active && onClear) && (
            <ExpandMoreRoundedIcon
              aria-hidden
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 150ms ease',
              }}
            />
          )}
        </ButtonBase>
        {active && onClear && (
          <Tooltip title="Clear">
            <IconButton
              size="small"
              aria-label={`Clear ${label.toLowerCase()} filter`}
              onClick={onClear}
              sx={{ mr: 0.5, p: 0.375, color: 'inherit', '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)' } }}
            >
              <CloseRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }
);

FilterPill.displayName = 'FilterPill';
